import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { RouterProvider } from '@tanstack/react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { ClerkProvider, useAuth } from '@clerk/clerk-react'
import { ConvexProviderWithClerk } from 'convex/react-clerk'
import { ConvexReactClient } from 'convex/react'
import { HelmetProvider } from 'react-helmet-async'
import { store } from './store/store'
import { router } from './lib/router'
import { queryClient } from './lib/queryClient'
import { StoreUserEffect } from './components/StoreUserEffect'
import { PostHogIdentify } from './components/PostHogIdentify'
import { initializePostHog } from './lib/posthog'
import './index.css'

// Initialize PostHog analytics
initializePostHog()

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
/**
 * Convex Database URL
 * 
 * Format: https://[deployment].convex.cloud
 * 
 * This URL is used for:
 * - Database queries and mutations
 * - Real-time subscriptions
 * - ConvexReactClient connection
 * 
 * NOTE: This is different from CONVEX_SITE_URL (.convex.site) which is used for:
 * - HTTP action endpoints
 * - Webhook handlers
 * - Public API endpoints
 * 
 * Set in .env.local and .env.production as VITE_CONVEX_URL
 */
const CONVEX_URL = import.meta.env.VITE_CONVEX_URL

if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY - Check .env.local file')
}

if (!CONVEX_URL) {
  throw new Error(
    'Missing VITE_CONVEX_URL - Check .env.local file\n\n' +
    'Format: https://[deployment].convex.cloud\n' +
    'This is the database URL (.cloud), not the HTTP endpoint URL (.site)\n\n' +
    'If you need to debug routes without Convex, edit index.html:\n' +
    'Change <script src="/src/main.tsx"> to <script src="/src/main.dev.tsx">'
  )
}

console.log('[Convex] Connecting to database:', CONVEX_URL)
const convex = new ConvexReactClient(CONVEX_URL)

const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML) {
  const root = createRoot(rootElement)
  root.render(
    <StrictMode>
      <HelmetProvider>
        <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
          <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
            <StoreUserEffect />
            <PostHogIdentify />
            <Provider store={store}>
              <QueryClientProvider client={queryClient}>
                <RouterProvider router={router} />
              </QueryClientProvider>
            </Provider>
          </ConvexProviderWithClerk>
        </ClerkProvider>
      </HelmetProvider>
    </StrictMode>
  )
}
